import { useCallback, useEffect, useState } from 'react';
import { TICKET_CONSTANTS } from '~/shared/constants';
import { useMyTickets } from './MyTicketsContext';

const { SEARCH, DATE, VALIDATION } = TICKET_CONSTANTS;

function validateDateFormat(dateString: string): boolean {
  if (dateString.length !== DATE.FORMAT_LENGTH) return false;

  const [day, month, year] = dateString.split('/');
  if (!day || !month || !year) return false;

  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  if (dayNum < VALIDATION.DAY_MIN || dayNum > VALIDATION.DAY_MAX) return false;
  if (monthNum < VALIDATION.MONTH_MIN || monthNum > VALIDATION.MONTH_MAX) {
    return false;
  }
  if (yearNum < VALIDATION.YEAR_MIN || yearNum > VALIDATION.YEAR_MAX) {
    return false;
  }

  const date = new Date(yearNum, monthNum - 1, dayNum);
  return (
    date.getDate() === dayNum &&
    date.getMonth() === monthNum - 1 &&
    date.getFullYear() === yearNum
  );
}

export function useMyTicketsSearch() {
  const { filters, setFilters } = useMyTickets();
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    if (searchInput.length >= SEARCH.MIN_CHARACTERS) {
      const timeoutId = setTimeout(() => {
        if (searchInput !== filters.search) {
          setFilters({ search: searchInput });
        }
      }, SEARCH.DEBOUNCE_MS);

      return () => clearTimeout(timeoutId);
    }

    if (searchInput.length === 0) {
      if (filters.search !== '') {
        setFilters({ search: '' });
      }
    }

    return undefined;
  }, [searchInput, filters.search, setFilters]);

  useEffect(() => {
    if (filters.search === '' && searchInput !== '') {
      setSearchInput('');
    }
  }, [filters.search]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  return {
    searchValue: searchInput,
    setSearchValue: handleSearchChange,
  };
}

export function useMyTicketsFilters() {
  const {
    filters,
    setFilters,
    clearFilters,
    clearIndividualFilter,
    hasActiveFilters,
  } = useMyTickets();

  const updateFilter = useCallback(
    (filterKey: keyof typeof filters, value: string | string[]) => {
      if (
        filterKey === 'search' ||
        filterKey === 'createdDateFrom' ||
        filterKey === 'createdDateTo'
      ) {
        setFilters({ [filterKey]: value as string });
        return;
      }

      if (typeof value === 'string') {
        if (value === '') {
          setFilters({ [filterKey]: [] });
        } else {
          const currentValues = filters[filterKey] as string[];
          if (currentValues.includes(value)) {
            setFilters({ [filterKey]: currentValues.filter(v => v !== value) });
          } else {
            setFilters({ [filterKey]: [...currentValues, value] });
          }
        }
      } else {
        setFilters({ [filterKey]: value });
      }
    },
    [setFilters, filters]
  );

  return {
    filters,
    updateFilter,
    clearFilters,
    clearIndividualFilter,
    hasActiveFilters,
  };
}

export function useMyTicketsDateFilters() {
  const { filters, setFilters, showDateFilters, setShowDateFilters } =
    useMyTickets();

  const [dateFromInput, setDateFromInput] = useState(filters.createdDateFrom);
  const [dateToInput, setDateToInput] = useState(filters.createdDateTo);

  useEffect(() => {
    if (dateFromInput.length === DATE.FORMAT_LENGTH) {
      const isValidDate = validateDateFormat(dateFromInput);
      if (isValidDate && dateFromInput !== filters.createdDateFrom) {
        setFilters({ createdDateFrom: dateFromInput });
      }
    } else if (dateFromInput.length === 0 && filters.createdDateFrom !== '') {
      setFilters({ createdDateFrom: '' });
    }
  }, [dateFromInput, filters.createdDateFrom, setFilters]);

  useEffect(() => {
    if (dateToInput.length === DATE.FORMAT_LENGTH) {
      const isValidDate = validateDateFormat(dateToInput);
      if (isValidDate && dateToInput !== filters.createdDateTo) {
        setFilters({ createdDateTo: dateToInput });
      }
    } else if (dateToInput.length === 0 && filters.createdDateTo !== '') {
      setFilters({ createdDateTo: '' });
    }
  }, [dateToInput, filters.createdDateTo, setFilters]);

  useEffect(() => {
    if (filters.createdDateFrom === '' && dateFromInput !== '') {
      setDateFromInput('');
    }
  }, [filters.createdDateFrom]);

  useEffect(() => {
    if (filters.createdDateTo === '' && dateToInput !== '') {
      setDateToInput('');
    }
  }, [filters.createdDateTo]);

  const updateDateFromInput = useCallback((value: string) => {
    setDateFromInput(value);
  }, []);

  const updateDateToInput = useCallback((value: string) => {
    setDateToInput(value);
  }, []);

  const toggleDateFilters = useCallback(() => {
    setShowDateFilters(!showDateFilters);
  }, [showDateFilters, setShowDateFilters]);

  return {
    createdDateFrom: dateFromInput,
    createdDateTo: dateToInput,
    showDateFilters,
    updateDateFromInput,
    updateDateToInput,
    toggleDateFilters,
  };
}
